

import { MortgageData, MonthlyPayment, ClosingCosts } from '@/types/calculator';
import { calculateMonthlyPropertyTax, calculateMonthlyPIDAssessment } from '@/components/calculator/utils/taxCalculations';
import { calculateMonthlyPMI } from '@/components/calculator/utils/insuranceCalculations';
import { calculateFundingFee } from '@/components/calculator/utils/fundingFeeCalculations';

export const calculateMortgageData = (data: MortgageData): MortgageData => {
  const loanAmount = data.homePrice - data.downPayment;
  const monthlyRate = data.interestRate / 100 / 12;
  const numberOfPayments = data.loanTerm * 12;
  
  let monthlyPayment = 0;
  if (monthlyRate > 0) {
    monthlyPayment = loanAmount * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  } else {
    monthlyPayment = loanAmount / numberOfPayments;
  }
  
  // Use the VA-aware property tax calculation utility with state-specific exemptions
  const monthlyPropertyTax = calculateMonthlyPropertyTax(
    data.homePrice,
    data.propertyTax || 2.00, // Default to 2.00% if not provided
    (data.vaDisabilityStatus as "none" | "partial" | "full" | null) || "none",
    data.vaDisabilityRatingPercentage,
    data.loanType as "conventional" | "fha" | "va" | "usda",
    data.state,
    data.veteranAge
  );
  
  // Calculate PID assessment separately (not exempt for VA disabled veterans)
  const monthlyPIDAssessment = data.includePID ? 
    calculateMonthlyPIDAssessment(data.homePrice, data.pidRate || 0) : 0;
  
  // Convert annual homeInsurance to monthly - this is the key fix
  const monthlyInsurance = data.homeInsurance ? 
    data.homeInsurance / 12 : 
    (data.homePrice * 0.003 / 12); // Default to 0.3% annually if not provided
  
  // Calculate down payment percentage and PMI using proper function
  const downPaymentPercentage = (data.downPayment / data.homePrice) * 100;
  const monthlyPMI = calculateMonthlyPMI(
    loanAmount,
    downPaymentPercentage,
    data.creditScore || 750, // Default credit score if not provided
    data.loanType as "conventional" | "fha" | "va" | "usda"
  );
  
  // Calculate funding fee using proper function that accounts for VA disability status
  const fundingFeeResult = calculateFundingFee(
    data.loanType as string,
    loanAmount,
    data.homePrice,
    data.downPayment,
    downPaymentPercentage,
    data.isFirstTimeHomebuyer || false,
    data.vaStatus,
    data.homebuyingHistory,
    data.vaDisabilityStatus
  );

  const closingCosts: ClosingCosts = {
    loanOrigination: data.closingCosts?.loanOriginationWaived ? 0 : loanAmount * 0.01,
    loanOriginationWaived: data.closingCosts?.loanOriginationWaived || false,
    builderName: data.closingCosts?.builderName,
    appraisalFee: 500,
    titleServices: data.homePrice * 0.001,
    governmentRecordingCharges: 300,
    prepaids: monthlyPropertyTax + monthlyInsurance,
    initialEscrow: (monthlyPropertyTax + monthlyInsurance) * 3,
    other: 500,
    fundingFee: fundingFeeResult.amount,
    sellerCreditAmount: 0, // Will be calculated below
    total: 0,
    totalCashNeeded: 0,
    appraisal: 500,
    inspection: 0,
    titleInsurance: data.homePrice * 0.001,
    lenderFees: loanAmount * 0.01,
    otherFees: 500,
    sellerCredit: data.sellerCredit || 0 // Store the percentage value
  };
  
  // Calculate base closing costs total first
  const baseCosts = closingCosts.loanOrigination + closingCosts.appraisalFee + 
    closingCosts.titleServices + closingCosts.governmentRecordingCharges + 
    closingCosts.prepaids + closingCosts.initialEscrow + closingCosts.other + 
    closingCosts.fundingFee;
  
  // Calculate seller credit amount from percentage (2% becomes $11,000 on $550,000 home)
  const sellerCreditAmount = data.sellerCredit ? 
    Math.min((data.sellerCredit / 100) * data.homePrice, baseCosts) : 0;
  
  closingCosts.sellerCreditAmount = sellerCreditAmount;
  closingCosts.total = baseCosts;
  closingCosts.totalCashNeeded = data.downPayment + baseCosts - sellerCreditAmount;
  
  const monthlyPaymentBreakdown: MonthlyPayment = {
    principal: monthlyPayment - (monthlyPayment * monthlyRate),
    interest: monthlyPayment * monthlyRate,
    taxes: monthlyPropertyTax + monthlyPIDAssessment, // Includes both property tax and PID
    insurance: monthlyInsurance, // Now properly calculated from annual value
    pmi: monthlyPMI,
    hoa: data.hoaFees || 0,
    total: monthlyPayment + monthlyPropertyTax + monthlyPIDAssessment + monthlyInsurance + monthlyPMI + (data.hoaFees || 0)
  };
  
  return {
    ...data,
    monthlyPayment: monthlyPaymentBreakdown,
    closingCosts
  };
};

